import React from "react";
import { motion } from "framer-motion";
import {
  CheckCircle2,
  AlertCircle,
  XCircle,
  Users,
  MapPin,
  Target,
  MessageCircle,
  FileCheck,
  Calendar,
  ExternalLink,
} from "lucide-react";
import { formatAudienceSize, parseAudienceSize } from "../../utils/profileCompleteness";
import { getPlatformBranding } from "./PlatformIcons";

/**
 * Generate clickable URL for a given platform and handle
 */
const generatePlatformUrl = (platform, handle, link) => {
  // If link is already provided, use it
  if (link && link !== handle) {
    return link;
  }

  const platformLower = platform?.toLowerCase() || "";
  
  // If handle already looks like a URL, return it
  if (handle && (handle.startsWith('http://') || handle.startsWith('https://'))) {
    return handle;
  }

  switch (platformLower) {
    case "whatsapp":
      // Format: https://wa.me/233249158582
      const cleanNumber = handle?.replace(/\D/g, ''); // Remove non-digits
      return cleanNumber ? `https://wa.me/${cleanNumber}` : null;
    
    case "tiktok":
      // Format: https://www.tiktok.com/@username
      const tiktokHandle = handle?.replace('@', '');
      return tiktokHandle ? `https://www.tiktok.com/@${tiktokHandle}` : null;
    
    case "instagram":
      // Format: https://www.instagram.com/username
      const igHandle = handle?.replace('@', '');
      return igHandle ? `https://www.instagram.com/${igHandle}` : null;
    
    case "facebook":
      // If handle is already a URL, return it; otherwise try to construct one
      if (handle?.includes('facebook.com')) {
        return handle;
      }
      const fbHandle = handle?.replace('@', '');
      return fbHandle ? `https://www.facebook.com/${fbHandle}` : null;
    
    case "telegram":
      // Format: https://t.me/username
      if (handle?.includes('t.me')) {
        return handle;
      }
      const tgHandle = handle?.replace('@', '');
      return tgHandle ? `https://t.me/${tgHandle}` : null;
    
    case "youtube":
      // Handle should already be a URL
      if (handle?.includes('youtube.com') || handle?.includes('youtu.be')) {
        return handle;
      }
      const ytHandle = handle?.replace('@', '');
      return ytHandle ? `https://www.youtube.com/@${ytHandle}` : null;
    
    case "website":
    case "podcast":
    case "newsletter":
      // These should already be full URLs
      return handle;
    
    case "offline":
      // No URL for offline channels
      return null;
    
    default:
      return null;
  }
};

const ChannelQualityAssessment = ({ promoter, channelRatings, onChannelRatingChange }) => {
  if (!promoter || !promoter.promotion_channels) {
    return (
      <div className="p-6 bg-white rounded-xl border border-gray-200">
        <h3 className="mb-2 text-lg font-semibold text-gray-900">B. Channel Quality Assessment</h3>
        <p className="text-sm text-gray-600">No promotion channels to assess</p>
      </div>
    );
  }

  const assessmentCriteria = [
    {
      id: "audience_relevance",
      label: "Audience Relevance",
      description: "How relevant is the audience to potential campaigns?",
      icon: Target,
    },
    {
      id: "engagement",
      label: "Engagement Quality",
      description: "Comments, replies, interactions observed",
      icon: MessageCircle,
    },
    {
      id: "content_clarity",
      label: "Content Clarity & Credibility",
      description: "Quality and trustworthiness of content",
      icon: FileCheck,
    },
    {
      id: "posting_consistency",
      label: "Posting Consistency",
      description: "Regular posting schedule observed",
      icon: Calendar,
    },
  ];

  const ratingOptions = [
    { value: "high", label: "High", icon: CheckCircle2, color: "green" },
    { value: "medium", label: "Medium", icon: AlertCircle, color: "yellow" },
    { value: "low", label: "Low", icon: XCircle, color: "red" },
  ];

  const getRatingIcon = (rating) => {
    const option = ratingOptions.find((opt) => opt.value === rating);
    if (!option) return null;
    const Icon = option.icon;
    return <Icon className={`w-4 h-4 text-${option.color}-600`} />;
  };

  const getRatingColor = (rating) => {
    const option = ratingOptions.find((opt) => opt.value === rating);
    return option ? option.color : "gray";
  };

  const getChannelOverallScore = (channelKey) => {
    const ratings = channelRatings[channelKey] || {};
    const scores = { high: 3, medium: 2, low: 1 };
    
    let total = 0;
    let count = 0;
    
    assessmentCriteria.forEach((criteria) => {
      const rating = ratings[criteria.id];
      if (rating) {
        total += scores[rating] || 0;
        count++;
      }
    });

    if (count === 0) return null;
    const avg = total / count;
    
    if (avg >= 2.5) return "high";
    if (avg >= 1.5) return "medium";
    return "low";
  };

  // Flatten all channels with platform info
  const allChannels = [];
  Object.entries(promoter.promotion_channels).forEach(([platform, channelArray]) => {
    if (Array.isArray(channelArray)) {
      channelArray.forEach((channel, index) => {
        allChannels.push({
          ...channel,
          platform,
          channelKey: `${platform}_${index}`,
          displayName: platform.charAt(0).toUpperCase() + platform.slice(1),
        });
      });
    }
  });

  const channelsWithRatings = allChannels.filter((channel) => {
    const ratings = channelRatings[channel.channelKey] || {};
    return Object.keys(ratings).length === assessmentCriteria.length;
  }).length;

  const allChannelsRated = channelsWithRatings === allChannels.length && allChannels.length > 0;

  return (
    <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
      <div className="flex gap-3 justify-between items-start pb-4 mb-6 border-b border-gray-100">
        <div>
          <h3 className="text-lg font-bold text-gray-900">Channel Quality Assessment</h3>
          <p className="mt-1 text-sm text-gray-500">
            Rate each channel on 4 key dimensions
          </p>
        </div>
        <div className="flex gap-2 items-center px-3 py-1.5 bg-blue-50 rounded-lg border border-blue-200">
          <span className="text-sm font-semibold text-blue-700">
            {channelsWithRatings} / {allChannels.length}
          </span>
        </div>
      </div>

      <div className="p-4 mb-6 bg-gray-50 rounded-lg border border-gray-200">
        <p className="mb-2 text-xs font-semibold text-gray-700">💡 Quality over Quantity</p>
        <p className="mb-3 text-xs leading-relaxed text-gray-600">
          A WhatsApp group with 200 active members may be more valuable than an Instagram page 
          with 10,000 inactive followers. Focus on engagement quality.
        </p>
        <p className="text-xs font-semibold text-gray-700 mb-1">How to Verify:</p>
        <p className="text-xs leading-relaxed text-gray-600">
          Click "Visit Channel" to review their content, engagement, and authenticity before rating.
        </p>
      </div>

      <div className="space-y-4">
        {allChannels.map((channel, idx) => {
          const ratings = channelRatings[channel.channelKey] || {};
          const overallScore = getChannelOverallScore(channel.channelKey);
          const audienceSize = parseAudienceSize(channel.audienceSize);
          const branding = getPlatformBranding(channel.platform);
          const PlatformIcon = branding.icon;

          return (
            <motion.div
              key={channel.channelKey}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
              className="p-4 bg-white rounded-lg border-2 border-gray-200 hover:border-gray-300 transition-all shadow-sm"
            >
              {/* Channel Header */}
              <div className="flex gap-3 items-start mb-4">
                <div className={`p-3 rounded-xl ${branding.bg} ${branding.text} shadow-md`}>
                  <PlatformIcon className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <div className="flex flex-wrap gap-2 items-center justify-between mb-2">
                    <div className="flex flex-wrap gap-2 items-center">
                      <h4 className="font-semibold text-gray-900">{channel.displayName}</h4>
                      {overallScore && (
                        <span
                          className={`px-2 py-0.5 text-xs font-medium rounded ${
                            overallScore === "high"
                              ? "bg-green-100 text-green-700"
                              : overallScore === "medium"
                              ? "bg-yellow-100 text-yellow-700"
                              : "bg-red-100 text-red-700"
                          }`}
                        >
                          Overall: {overallScore.charAt(0).toUpperCase() + overallScore.slice(1)}
                        </span>
                      )}
                    </div>
                    {channel.handle && (() => {
                      const url = generatePlatformUrl(channel.platform, channel.handle, channel.link);
                      return url ? (
                        <a
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`flex gap-1.5 items-center px-4 py-2 text-xs font-semibold rounded-lg transition-all shadow-sm ${branding.bg} ${branding.hover} ${branding.text}`}
                        >
                          <PlatformIcon className="w-4 h-4" />
                          Visit Channel
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      ) : null;
                    })()}
                  </div>

                  {/* Channel Details */}
                  <div className="grid grid-cols-2 gap-3 mb-3 text-xs md:grid-cols-4">
                    {channel.audienceSize && (
                      <div className="flex gap-1 items-center text-gray-600">
                        <Users className="w-3 h-3" />
                        <span>{formatAudienceSize(channel.audienceSize)}</span>
                      </div>
                    )}
                    {channel.location && (
                      <div className="flex gap-1 items-center text-gray-600">
                        <MapPin className="w-3 h-3" />
                        <span>{channel.location}</span>
                      </div>
                    )}
                    {channel.purpose && (
                      <div className="flex gap-1 items-center text-gray-600">
                        <Target className="w-3 h-3" />
                        <span>{channel.purpose}</span>
                      </div>
                    )}
                  </div>

                  {/* Clickable Handle/Link */}
                  {channel.handle && (
                    <div className="p-3 mb-3 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="mb-2 text-xs font-semibold text-gray-700">
                        {channel.platform.toLowerCase() === "offline" ? "📍 Location" : "🔗 Channel Link"}
                      </p>
                      {(() => {
                        const url = generatePlatformUrl(channel.platform, channel.handle, channel.link);
                        const branding = getPlatformBranding(channel.platform);
                        const PlatformIcon = branding.icon;
                        
                        if (channel.platform.toLowerCase() === "offline") {
                          return (
                            <div className="flex gap-2 items-center px-3 py-2 text-sm text-gray-700 bg-white rounded-lg border border-gray-200">
                              <PlatformIcon className="w-4 h-4 flex-shrink-0 text-gray-500" />
                              <span className="flex-1">{channel.handle}</span>
                              <span className="px-2 py-0.5 text-xs font-medium text-gray-600 bg-gray-100 rounded">
                                Offline
                              </span>
                            </div>
                          );
                        }
                        return url ? (
                          <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`flex gap-2 items-center px-3 py-2 text-sm font-medium rounded-lg transition-all group ${branding.bg} ${branding.hover} ${branding.text}`}
                          >
                            <PlatformIcon className="w-4 h-4 flex-shrink-0" />
                            <span className="truncate flex-1">{channel.handle}</span>
                            <ExternalLink className="w-4 h-4 flex-shrink-0 opacity-70 group-hover:opacity-100 transition-opacity" />
                          </a>
                        ) : (
                          <div className="flex gap-2 items-center px-3 py-2 text-sm text-gray-600 bg-white rounded-lg border border-gray-200">
                            <PlatformIcon className="w-4 h-4 flex-shrink-0 text-gray-400" />
                            <span className="truncate">{channel.handle}</span>
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* Mobile App Stores */}
                  {channel.stores && Object.keys(channel.stores).length > 0 && (
                    <div className="p-3 mb-3 bg-teal-50 rounded-lg border border-teal-200">
                      <p className="mb-2 text-xs font-semibold text-teal-900">📱 Available On:</p>
                      <div className="flex flex-wrap gap-2">
                        {channel.stores.play_store && (
                          <a
                            href={channel.stores.play_store}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex gap-1 items-center px-3 py-1.5 text-xs font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors"
                          >
                            <ExternalLink className="w-3 h-3" />
                            Play Store
                          </a>
                        )}
                        {channel.stores.apple_store && (
                          <a
                            href={channel.stores.apple_store}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex gap-1 items-center px-3 py-1.5 text-xs font-medium text-white bg-black rounded-lg hover:bg-gray-900 transition-colors"
                          >
                            <ExternalLink className="w-3 h-3" />
                            App Store
                          </a>
                        )}
                        {channel.stores.huawei && (
                          <a
                            href={channel.stores.huawei}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex gap-1 items-center px-3 py-1.5 text-xs font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                          >
                            <ExternalLink className="w-3 h-3" />
                            Huawei
                          </a>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Assessment Criteria */}
                  <div className="space-y-2">
                    {assessmentCriteria.map((criteria) => {
                      const CriteriaIcon = criteria.icon;
                      const currentRating = ratings[criteria.id];

                      return (
                        <div key={criteria.id} className="p-3 bg-white rounded border border-gray-200">
                          <div className="flex gap-2 items-center mb-2">
                            <CriteriaIcon className="w-4 h-4 text-gray-500" />
                            <span className="text-sm font-medium text-gray-900">{criteria.label}</span>
                          </div>
                          <p className="mb-2 text-xs text-gray-600">{criteria.description}</p>
                          <div className="flex gap-2">
                            {ratingOptions.map((option) => (
                              <button
                                key={option.value}
                                onClick={() =>
                                  onChannelRatingChange(channel.channelKey, criteria.id, option.value)
                                }
                                className={`flex-1 flex items-center justify-center gap-1 px-3 py-2 text-xs font-medium rounded transition-all ${
                                  currentRating === option.value
                                    ? `bg-${option.color}-100 text-${option.color}-700 border-2 border-${option.color}-300`
                                    : "bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100"
                                }`}
                              >
                                {currentRating === option.value && getRatingIcon(option.value)}
                                {option.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {allChannels.length === 0 && (
        <div className="p-4 text-center bg-yellow-50 rounded-lg border border-yellow-200">
          <p className="text-sm text-yellow-800">No promotion channels declared</p>
        </div>
      )}

      {!allChannelsRated && allChannels.length > 0 && (
        <div className="flex gap-3 items-start p-4 mt-4 bg-yellow-50 rounded-lg border border-yellow-200">
          <AlertCircle className="flex-shrink-0 w-5 h-5 text-yellow-600" />
          <div>
            <h4 className="mb-1 font-semibold text-yellow-900">Incomplete Assessment</h4>
            <p className="text-sm text-yellow-800">
              Please rate all {allChannels.length} channel{allChannels.length !== 1 ? "s" : ""} on all 4 criteria 
              before proceeding with approval. ({channelsWithRatings} / {allChannels.length} channels fully rated)
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChannelQualityAssessment;
